<!--
 * 文件名：labelExecution.vue
 * 描述：标注任务执行页（v26.3.3 数据标注·医生端）。
 *       从任务执行列表"去完成"进入，页内以 micro-app（iframe 沙箱档）嵌入 Label Studio 标注页，
 *       免登=token 经 data 内存通道注入（LS 侧桥接与徐鹏对齐，联调首验）。
 * 数据层（09-11）：GET /label-task/label-url 由 FBT 后端自建（F0），返回 LS 项目页地址；
 *       F0 未就绪时走 labelTaskApi 本地 mock（MOCK_ENABLED）；
 *       未就绪时显示同步说明，可从原页头返回后重进，不通过新窗口绕过当前权限。
-->
<template>
  <div class="label-execution" >
    <div class="header">
      <div class="back" @click="goBack">返回</div>
      <div class="task-title">{{ isView ? '标注详情-' : '' }}{{ $route.query.taskTitle || '标注任务' }}</div>
    </div>
    <div v-if="entry && entry.mode != null && entry.lsSyncState !== 'SYNCED'" class="fallback">
      <p>审核工具状态同步中，请稍后重新进入</p>
    </div>
    <div class="stage" v-else-if="entry && entry.canEnter === true && labelUrl">
      <label-tool-embed
        tool-type="labelStudio"
        :url="labelUrl"
        :task-id="taskId"
        :is-view="isView"
        :data="{ targetRoute: targetRoute }"
        @datachange="onSubData"
        @submit-success="onSubmitSuccess"
      ></label-tool-embed>
    </div>
    <div class="fallback" v-else-if="!loading">
      <p>{{ entry ? entry.message : entryError || "当前标注入口暂不可用，请返回列表后重新进入" }}</p>
    </div>
  </div>
</template>

<script>
import { entryKey, entryRouteKey } from "@/views/mixins/labelEntrySync";
import LabelToolEmbed from '@/views/certManagement/components/LabelToolEmbed.vue';
import { CONTRACT_URLS, MOCK_ENABLED, mockLabelUrl } from '@/views/certManagement/components/labelTaskApi';

export default {
  name: 'labelExecution',
  components: { LabelToolEmbed },
  data() {
    return {
      loading: false,
      entry: null,
      entryError: "",
      labelUrl: '',
      targetRoute: '',
      taskId: '',
    };
  },
  computed: {
    /**
     * 捕获路由定位，变更时立即关闭旧编辑器。
     * 参数：无。
     * @returns {string} 当前业务定位键；子应用路由回写不代表更换任务。
     */
    entryRouteKey() {
      return entryRouteKey(this.$route.query);
    },
    // 当前服务端授权决定只读；旧路由不能把已经提交的内容重新开放编辑。
    isView() {
      return !(this.entry && this.entry.canEdit === true);
    },
  },
  created() {
    this.loadLabelUrl();
  },
  watch: {
    /**
     * 捕获路由定位，变更时立即关闭旧编辑器。
     * 参数：无。
     * @returns {void} 无；立即关闭旧编辑器并请求页面重建。
     */
    entryRouteKey() {
      this._entryDisposed = true;
      this.entry = null;
      this.labelUrl = '';
      // iframe沙箱避免复用旧任务，沿用审核详情的整页重建方式。
      window.location.reload();
    },
  },
  /**
   * 销毁页面时使旧请求失效。
   * 参数：无。
   * @returns {void} 无。
   */
  beforeDestroy() {
    this._entryDisposed = true;
    this._entryRequest = (this._entryRequest || 0) + 1;
  },
  methods: {
    entryKey,
    // 固定返回执行列表；路由完成前保留子应用，避免提前卸载回写历史并取消返回。
    goBack() {
      this._entryDisposed = true;
      this._entryRequest = (this._entryRequest || 0) + 1;
      this.$router.replace('/taskExecutionList');
    },
    // 同一只读入口核对当前范围、阶段、权限及工具实际就绪。
    loadLabelUrl() {
      const request = this._entryRequest = (this._entryRequest || 0) + 1;
      const binding = this.entryKey();
      /**
       * 仅允许当前路由、身份和请求代次接收响应。
       * 参数：无。
       * @returns {boolean} 页面未销毁、请求代次未被替换且路由及身份绑定键完全相同时为true；任一变化为false。
       */
      const isCurrent = () => !this._entryDisposed && request === this._entryRequest && binding === this.entryKey();
      const taskDetailGuid = this.$route.query.taskDetailGuid;
      if (!taskDetailGuid) {
        this.$message.error('缺少任务参数');
        return;
      }
      this.entry = null;
      this.labelUrl = "";
      this.entryError = "";
      this.loading = true;
      const done = (data) => {
        if (!isCurrent()) return;
        this.loading = false;
        this.entry = data || null;
        if (data && data.url) {
          this.labelUrl = data.url;
          this.targetRoute = data.targetRoute || '';
          this.taskId = data.taskId || '';
        }
      };
      if (MOCK_ENABLED) {
        mockLabelUrl(this.$route.query.taskMainGuid).then(done).catch(() => (this.loading = false));
        return;
      }
      const staffGuid = this.$route.query.staffGuid;
      const urlQuery = `taskDetailGuid=${taskDetailGuid}` + (staffGuid ? `&staffGuid=${staffGuid}` : '');
      this.api
        .get(`/${serverConfig.msSalesPerformService}${CONTRACT_URLS.labelUrl}?${urlQuery}&tenantGuid=${encodeURIComponent(localStorage.getItem('guid') || '')}`)
        .then((res) => {
          if (res.data.code == serverConfig.REQUIRE_SUCCESS_CODE) done(res.data.data);
          else { if (isCurrent()) { this.loading = false; this.entryError = "状态核验失败，请返回列表后重新进入"; } }
        })
        .catch(() => {
          if (!isCurrent()) return;
          this.loading = false;
          this.entryError = "状态核验失败，请返回列表后重新进入";
        });
    },
    // 子应用（LS）回传数据
    onSubData(data) {
      // 预留：LS 桥接后可在此接收标注完成/状态事件驱动列表刷新
      console.log('label tool datachange', data);
    },
    /**
     * 标注工具提交成功后返回执行列表，后台继续完成状态同步。
     * @param {*} data 标注工具的提交成功通知。
     * @returns {void} 无。
     */
    onSubmitSuccess(data) {
      if (this._entryDisposed || !this.entry || (data && data.projectId != null && String(data.projectId) !== String(this.entry.projectId))) return;
      this.goBack();
    },

  },
};
</script>

<style scoped>
.label-execution {
  background: #fff;
  min-height: 100%;
}
.header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #e5e5e5;
  text-align: left;
}
/* 2026-10-08 样式反馈：返回按钮加边框化（同"查看审核信息"观感，灰边框+文字 #212121，
   与 labelTemplateEdit 标题条返回按钮同一标准；原 teal 文字链接无边框被圈注） */
.back {
  color: #212121;
  cursor: pointer;
  font-size: 12px;
  width: 60px;
  height: 26px;
  line-height: 24px;
  text-align: center;
  padding: 0;
  box-sizing: border-box;
  border: 1px solid rgba(217, 217, 217, 1);
  border-radius: 2px;
  background: #fff;
}
.task-title {
  margin-left: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #212121;
}
.stage {
  height: calc(100vh - 120px);
}
.fallback {
  padding: 60px 0;
  text-align: center;
  color: #606266;
}
.fallback .tip {
  font-size: 13px;
  color: #909399;
  margin: 8px 0 16px;
}
</style>
